import { DropdownProps } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Skeleton } from 'primereact/skeleton';
import { classNames } from 'primereact/utils';
import { ChangeEvent, useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ErrorMessage from '../../../../../../../../../../../../../../components/error-message';
import { useListManufacturers } from '../../../../../../../../../../../../../../hooks/api/manufacturer';
import useNotificationContext from '../../../../../../../../../../../../../../hooks/notification/useNotificationContext';
import { ToastSeverity } from '../../../../../../../../../../../../../../shared/enums/toast-severity';
import { IManufacturer } from '../../../../../../PcfWizard.type';
import styles from './ManufacturerDropdown.module.css';
import Menu from './Menu';

interface ManufacturerDropdownProps extends DropdownProps {
  name: string;
  label?: string;
}

const ManufacturerDropDown = (props: ManufacturerDropdownProps) => {
  const { name, label } = props;
  const { control } = useFormContext();
  const { t } = useTranslation();
  const { notify } = useNotificationContext();

  const onFetchManufacturerListError = () => {
    notify({
      severity: ToastSeverity.ERROR,
      detail: t('toastMessages.getManufacturers.states.failure.detail'),
    });
  };

  const { data: manufacturers, isLoading } = useListManufacturers({
    onError: onFetchManufacturerListError,
  });

  const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>('');
  const [filteredManufacturers, setFilteredManufacturers] = useState<IManufacturer[]>([]);

  useEffect(() => {
    setFilteredManufacturers(manufacturers || []);
  }, [manufacturers]);

  const {
    field: { value: inputValue, ref, onChange },
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
  });

  const onInputBlur = (): void => {
    setIsMenuVisible(false);
  };

  const onInputMouseDown = (): void => {
    setIsMenuVisible(true);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    // Close the menu if "Esc" is pressed
    if (e.key === 'Escape') {
      setIsMenuVisible(false);
    }
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;

    if (!isMenuVisible) setIsMenuVisible(true);

    setSearchString(value);

    if (value !== inputValue?.name) {
      // Unselect the value if user types something in the input right after selecting an option
      onChange(null);
    }

    // Filter options based on search string
    setFilteredManufacturers(() => {
      if (!value) {
        // If the search string is empty, return the initial menu back
        return manufacturers || [];
      }

      // Filter by search string
      const _filteredManufacturers = manufacturers?.filter((manufacturer) => {
        return manufacturer?.name?.toLowerCase()?.includes(value?.trim()?.toLowerCase());
      });

      return _filteredManufacturers || [];
    });
  };

  // Function to what to show on the dropdown. Considers the selected item and/or search string
  const determineDropdownValue = (): string | undefined => {
    // Find the corresponding manufacturer object based on the inputValue (which is the id)
    const manufacturer = manufacturers?.find((m) => {
      return m.id === inputValue;
    });

    if (manufacturer?.name) {
      return manufacturer?.name;
    }

    if (searchString) {
      return searchString;
    }

    return '';
  };

  const onSearchMouseDown = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
  };

  const onTriggerMouseDown = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setIsMenuVisible(!isMenuVisible);
  };

  const onItemMouseDown = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
  };

  const onItemSelect = (item: IManufacturer): void => {
    onChange(item.id);
    setSearchString(item?.name || '');
    setIsMenuVisible(false);
  };

  if (isLoading) {
    return <Skeleton className='w-full mb-4' height='45px' />;
  }

  return (
    <div className='mb-2'>
      <div className='p-fluid mb-1' onBlur={onInputBlur}>
        <div
          className={classNames(
            'flex flex-row align-items-center p-float-label',
            styles['input-wrapper'],
          )}
          onMouseDown={onInputMouseDown}
          onKeyDown={onKeyDown}
          ref={ref}
        >
          <InputText
            ref={ref}
            name={name}
            type='text'
            onChange={onInputChange}
            value={determineDropdownValue()}
            style={{ textIndent: '2rem' }}
            className={classNames('line-height-1 text-overflow-ellipsis w-full', {
              'p-invalid': error,
            })}
          />
          <label htmlFor={name}>{label ?? ''}</label>

          {/* Search Icon */}
          <div
            className='flex align-items-center px-2 absolute left-0 mx-3 text-gray-400 z-1'
            onMouseDown={onSearchMouseDown}
          >
            <i className='pi pi-search' />
          </div>

          {/* Trigger Icon */}
          <div
            className='flex align-items-center  absolute right-0 px-2 py-2 mr-3 text-gray-400 z-1'
            onMouseDown={onTriggerMouseDown}
          >
            <i className={classNames('pi', isMenuVisible ? 'pi-angle-up' : 'pi-angle-down')} />
          </div>
        </div>
        {/* Dropdown Menu */}
        {isMenuVisible && (
          <Menu
            filteredManufacturers={filteredManufacturers}
            onItemMouseDown={onItemMouseDown}
            onItemSelect={onItemSelect}
          />
        )}
      </div>
      {invalid && <ErrorMessage message={error?.message ?? ''} />}
    </div>
  );
};

export default ManufacturerDropDown;
